// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useQuery } from '@tanstack/react-query';
import { listAnomalies } from '../services/anomaliesApiService';
import { enrichRecordToAlarm } from '../services/alarmEnrichment';
import { queryKeys } from '../lib/queryKeys';
import type { Alarm } from '../types/alarm';

/**
 * Fetch a single anomaly, narrowed by the columns the detail route carries.
 *
 * `alarm_code` alone is not a row identity — it is the same value as
 * `alarm_rule_id`, i.e. the RULE, which is why AEM 409s on promote when a code is
 * "ambiguous for this site_id". The consolidated alarm-api exposes `alarm_id`,
 * the per-row lifecycle identifier, and the BFF forwards it, so when the link
 * carries one the lookup is exact. `equipment_id` remains a narrowing fallback
 * for rows without an `alarm_id` (and for links predating it). All are documented
 * equality filters on the anomalies endpoint.
 */
export function useAnomaly(
  siteId?: string,
  alarmCode?: string,
  equipmentId?: string,
  alarmId?: string
) {
  const query = useQuery({
    queryKey: queryKeys.anomaly(siteId, alarmCode, equipmentId, alarmId),
    enabled: !!siteId && !!alarmCode,
    queryFn: async (): Promise<Alarm | null> => {
      const page = await listAnomalies(siteId as string, {
        alarmCode,
        equipmentId,
        alarmId,
        pageSize: 1,
      });
      const record = page.data[0];
      if (!record) return null;
      return enrichRecordToAlarm(siteId as string, record, { logTag: 'useAnomaly' });
    },
  });

  return { ...query, anomaly: query.data ?? undefined };
}
